import { useQuery } from '@tanstack/react-query';
import {
  hasCouponHoldingCondition,
  resolveCouponHolderIds,
  type SegmentEvalContext,
  type SegmentSpec,
} from '@kawai/shared';
import { fetchCouponUsagesByCouponIds } from '../repositories/lottery.js';

/**
 * M-166: セグメント条件 `couponHolding` の判定材料（lineUserId 集合）を解決する。
 *
 * - 条件が無いときは何も読まず `{ ctx: undefined, isLoading: false }`
 * - 読込中は `ctx` が undefined → `matchesSegment` は fail-closed で 0 人（過大に見せない）。
 *   呼出元は `isLoading` で「計算中」を出し、`error` のときは 0 人を事実として見せず
 *   「読み込めなかった」を出す（配信作成は送信も止める）
 * - 同じ docs を functions と同じ純関数 `resolveCouponHolderIds` に通すので、人数プレビューと
 *   即時・予約配信の母集団が一致する
 *
 * cache key は couponIds（整列）+ state。state が変わっても usages の読み直しは要らないが、
 * 集合の派生を key に含めて memo を単純にする（読取は react-query の staleTime 内で共有される）。
 */
export function useCouponHolders(
  tenantId: string | null | undefined,
  segment: SegmentSpec,
): { ctx: SegmentEvalContext | undefined; isLoading: boolean; error: Error | null } {
  const spec = hasCouponHoldingCondition(segment) ? segment.couponHolding : undefined;
  const ids = spec ? [...spec.couponIds].sort() : [];
  const q = useQuery({
    queryKey: ['promo', 'segment', 'couponHolders', tenantId, ids, spec?.state ?? null],
    queryFn: async () => {
      const usages = await fetchCouponUsagesByCouponIds(tenantId as string, ids);
      return resolveCouponHolderIds(usages, spec as NonNullable<typeof spec>);
    },
    enabled: !!tenantId && !!spec,
    staleTime: 60 * 1000,
  });
  if (!spec) return { ctx: undefined, isLoading: false, error: null };
  // 読取失敗は握りつぶさない — fail-closed で 0 人になるだけだと「本当に 0 人」と区別できず、
  // 予約送信はサーバ側で正しく N 人に届く（プレビューと実配信の黙った不一致）。
  return {
    ctx: q.data ? { couponHolders: q.data } : undefined,
    isLoading: q.isLoading,
    error: q.error ?? null,
  };
}
